import { useState } from "react";
import { format, parseISO, isValid, addDays } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Drop-in replacement for <Input type="date">: value/onChange stay "YYYY-MM-DD" strings so
// backend formats and stored values are untouched. Shortcuts: Today, Tomorrow, Clear.
export default function DatePicker({ value, onChange, placeholder = "Select date", className, disabled, min, max, error, "data-testid": testId, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const d = value ? parseISO(String(value).slice(0, 10)) : null;
  const valid = d && isValid(d);
  const pick = (date) => { onChange(date ? format(date, "yyyy-MM-dd") : ""); setOpen(false); };
  const minD = min ? parseISO(min) : null; const maxD = max ? parseISO(max) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testId} aria-invalid={!!error}
          className={cn("flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50",
            error ? "border-rose-500" : "border-input", !valid && "text-muted-foreground", className)}>
          <span className="flex items-center gap-2 truncate"><CalendarIcon className="w-4 h-4 shrink-0 opacity-70" />{valid ? format(d, "EEE, d MMM yyyy") : placeholder}</span>
          {valid && allowClear && !disabled && <X className="w-3.5 h-3.5 opacity-60 hover:opacity-100" onClick={(e) => { e.stopPropagation(); pick(null); }} data-testid={testId ? `${testId}-clear` : undefined} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={testId ? `${testId}-popover` : undefined}>
        <div className="flex gap-1 p-2 border-b border-border">
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => pick(new Date())} data-testid={testId ? `${testId}-today` : undefined}>Today</Button>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => pick(addDays(new Date(), 1))} data-testid={testId ? `${testId}-tomorrow` : undefined}>Tomorrow</Button>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => pick(addDays(new Date(), 30))}>+30 days</Button>
        </div>
        <Calendar mode="single" selected={valid ? d : undefined} defaultMonth={valid ? d : undefined} onSelect={(date) => date && pick(date)}
          disabled={(date) => (minD && date < minD) || (maxD && date > maxD)} initialFocus />
      </PopoverContent>
    </Popover>
  );
}
